import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, TextField, Button, Paper, IconButton, Tooltip, MenuItem, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/EditOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpenOutlined";
import HistoryIcon from "@mui/icons-material/History";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import LoginHistoryDialog from "./LoginHistoryDialog";
import { BRAND } from "../../styles/theme";
import type { CollectorCtx } from "../../pages/CollectorDetail";
import { CollectorUser, USER_ROLES, listUsers, addUser } from "../../services/collectors";
import { getCollectorSummary } from "../../services/collectorStats";
import { CollectorAccess, UserAccess, getCollectorAccess, setUserCollectors, isLocked, unlockUser } from "../../services/userAccess";
import { StatusChip, MetaChip, fmtDateTime } from "./chips";
import { accountsMatch } from "../../lib/userAccounts";
import { CollectorChips, CollectorPicker, VendorOption, vendorOptions } from "./CollectorPicker";

const squareInput = { "& .MuiOutlinedInput-root": { borderRadius: 1 } };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const ROLE_KIND: Record<string, "primary" | "info" | "neutral"> = { main: "primary", admin: "info" };

/**
 * CP-479: el rol se cambia con el mismo POST login_user del alta (CoreDB actualiza
 * `roles` si el email ya existe en el collector, como hace WA1). CoreDB rechaza
 * cambiar un usuario main; los usuarios token (sin email) son siempre `pos`.
 */
function roleChangeBlocked(u: CollectorUser): string | null {
  if (u.role === "main") return "A main user's role can't be changed";
  if (!u.email.includes("@")) return "Token users are always pos";
  return null;
}

export default function UsersTab({ ctx }: { ctx: CollectorCtx }) {
  const { collector, notify } = ctx;
  const [sp, setSp] = useSearchParams();
  const q    = sp.get("q") ?? "";
  const role = sp.get("role") ?? "";
  const setParam = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };

  const [rows, setRows]       = useState<CollectorUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Collectors adicionales (multi_iid) — lambda user_access de webadmin2. Si falla
  // la lista de usuarios igual se muestra; solo se pierde la columna/edición.
  const [access, setAccess]       = useState<CollectorAccess | null>(null);
  const [accessErr, setAccessErr] = useState<string | null>(null);
  const [options, setOptions]     = useState<VendorOption[]>([]);
  const [editing, setEditing]     = useState<UserAccess | null>(null);
  const [unlocking, setUnlocking] = useState<CollectorUser | null>(null);
  const [history, setHistory]     = useState<string | null>(null);
  const [roleUser, setRoleUser]   = useState<CollectorUser | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null); setAccessErr(null);
    const acc = getCollectorAccess(collector.id).then(setAccess, (e) => { setAccess(null); setAccessErr(String(e?.message ?? e)); });
    try { setRows(await listUsers(collector.id)); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { await acc; setLoading(false); }
  }, [collector.id]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { getCollectorSummary().then((s) => setOptions(vendorOptions(s.items)), () => setOptions([])); }, []);

  const accessByUid = useMemo(() => new Map((access?.users ?? []).map((u) => [u.uid, u])), [access]);
  // El backend confirma contra collection_config qué iids existen: completa las
  // opciones si el resumen de collector_stats no cargó o está desactualizado.
  const known = useMemo(() => {
    const have = new Set(options.map((o) => o.iid));
    const more = Object.entries(access?.collectors ?? {}).filter(([i]) => !have.has(Number(i))).map(([i, ids]) => ({ iid: Number(i), ids }));
    return [...options, ...more];
  }, [options, access]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((u) => (!role || u.role === role) && (!s || u.email.toLowerCase().includes(s) || accountsMatch(u.accounts, s)));
  }, [rows, q, role]);

  return (
    <>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <TextField size="small" label="Search" placeholder="Email or customer id…" value={q} onChange={(e) => setParam("q", e.target.value)} sx={{ width: 320 }} />
        <TextField size="small" select label="Role" value={role} onChange={(e) => setParam("role", e.target.value)} sx={{ width: 170 }}>
          <MenuItem value="">All roles</MenuItem>
          {USER_ROLES.map((r) => <MenuItem key={r.key} value={r.key}>{r.key}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">{filtered.length} of {rows.length}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ height: 40 }}>Add user</Button>
      </Stack>
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {accessErr && <Alert severity="warning" onClose={() => setAccessErr(null)} sx={{ mb: 2 }}>Could not load collector access: {accessErr}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <Tooltip title="Customer ids of the accounts the user is restricted to in the vendor portal (All = no restriction)"><span>Accounts</span></Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Collectors</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last login</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Failed attempts</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No vendor web users. Add the first one with role <b>main</b> so they can manage the rest.</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No users match.</TableCell></TableRow>
            ) : filtered.map((u) => { const a = accessByUid.get(`${u.email.toLowerCase()}#${u.channel}`); return (
              <TableRow key={u.email + u.channel} hover>
                <TableCell>{u.email}{u.channel && <MetaChip label={u.channel} />}</TableCell>
                <TableCell>
                  <Tooltip title={USER_ROLES.find((r) => r.key === u.role)?.description ?? ""}>
                    <span><StatusChip kind={ROLE_KIND[u.role] ?? "neutral"} label={u.role || "—"} /></span>
                  </Tooltip>
                </TableCell>
                <TableCell><AssignedAccounts ids={u.accounts} /></TableCell>
                <TableCell>{a ? <CollectorChips iids={a.collectors} primary={a.iid} options={known} /> : "—"}</TableCell>
                <TableCell>{fmtDateTime(u.lastLogin)}{u.appVersion && <MetaChip label={`v${u.appVersion}`} />}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace", color: u.failedAttempts > 0 ? "error.main" : undefined, fontWeight: u.failedAttempts > 0 ? 700 : 400 }}>
                  {u.failedAttempts}{isLocked(u.failedAttempts) && <Box component="span" sx={{ ml: 1 }}><StatusChip kind="bad" label="locked" /></Box>}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title="Login history"><IconButton size="small" onClick={() => setHistory(u.email)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                  {isLocked(u.failedAttempts) && (
                    <Tooltip title="Unlock user"><IconButton size="small" color="error" onClick={() => setUnlocking(u)}><LockOpenIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  <Tooltip title={roleChangeBlocked(u) ?? "Change role"}><span>
                    <IconButton size="small" disabled={!!roleChangeBlocked(u)} onClick={() => setRoleUser(u)}><BadgeIcon fontSize="small" /></IconButton>
                  </span></Tooltip>
                  <Tooltip title="Edit collectors"><span>
                    <IconButton size="small" disabled={!a || a.iid == null || a.iid <= 0} onClick={() => a && setEditing(a)}><EditIcon fontSize="small" /></IconButton>
                  </span></Tooltip>
                </TableCell>
              </TableRow>
            ); })}
          </TableBody>
        </Table>
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh"><span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
        </Stack>
      </TableContainer>

      <AddUserDialog open={addOpen} ctx={ctx} existing={rows} onClose={() => setAddOpen(false)}
        vendorIid={access?.vendor_iid ?? null} options={known}
        onDone={async (msg) => { setAddOpen(false); notify(msg); await load(); }} />
      <LoginHistoryDialog email={history} onClose={() => setHistory(null)} />
      <UnlockDialog user={unlocking} onClose={() => setUnlocking(null)}
        onDone={async () => { const e = unlocking?.email; setUnlocking(null); notify(`${e} unlocked — they can request a new login code`); await load(); }} />
      <EditCollectorsDialog user={editing} options={known} onClose={() => setEditing(null)}
        onDone={async () => { setEditing(null); notify("Collectors updated"); await load(); }} />
      <ChangeRoleDialog user={roleUser} collectorId={collector.id} onClose={() => setRoleUser(null)}
        onDone={async (role) => { const e = roleUser?.email; setRoleUser(null); notify(`${e} is now ${role}`); await load(); }} />
    </>
  );
}

function ChangeRoleDialog({ user, collectorId, onClose, onDone }: {
  user: CollectorUser | null; collectorId: string; onClose: () => void; onDone: (role: string) => Promise<void>;
}) {
  const [role, setRole] = useState("standard");
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  useEffect(() => { if (user) { setRole(user.role || "standard"); setErr(null); } }, [user]);

  async function save() {
    if (!user) return;
    setBusy(true); setErr(null);
    try { await addUser(collectorId, user.email.toLowerCase(), role); await onDone(role); }
    catch (ex: any) { setErr(String(ex?.message ?? ex)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={!!user} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Change role<Typography variant="body2" color="text.secondary">{user?.email} · {collectorId}</Typography></DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} mt={1}>
          <RadioGroup value={role} onChange={(ev) => setRole(ev.target.value)}>
            {USER_ROLES.map((r) => (
              <FormControlLabel key={r.key} value={r.key} disabled={busy} control={<Radio size="small" />}
                sx={{ alignItems: "flex-start", mx: 0, my: 0.5, p: 1, border: 1, borderColor: role === r.key ? BRAND.primary : "divider", borderRadius: 2, bgcolor: role === r.key ? "rgba(91,47,235,0.05)" : undefined }}
                label={<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>{r.key}{r.key === user?.role && " (current)"}</Typography><Typography variant="caption" color="text.secondary">{r.description}</Typography></Box>} />
            ))}
          </RadioGroup>
          {role === "main" && role !== user?.role && (
            <Alert severity="warning">A main user can manage the other users, and its role can't be changed again from Web Admin.</Alert>
          )}
          <Typography variant="caption" color="text.secondary">Applies on the user's next login. The change is logged in CoreDB.</Typography>
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !user || role === user.role}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>{busy ? "Saving…" : "Change role"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function AddUserDialog({ open, ctx, existing, vendorIid, options, onClose, onDone }: {
  open: boolean; ctx: CollectorCtx; existing: CollectorUser[]; vendorIid: number | null; options: VendorOption[];
  onClose: () => void; onDone: (msg: string) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole]   = useState("standard");
  const [extra, setExtra] = useState<number[]>([]);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState<string | null>(null);
  useEffect(() => { if (open) { setEmail(""); setRole("standard"); setExtra([]); setErr(null); } }, [open]);
  const e = email.trim().toLowerCase();
  // CP-479: como en WA1, dar de alta un email que ya existe cambia su rol (CoreDB
  // actualiza `roles`). Main y usuarios token no se pueden cambiar.
  const current = existing.find((u) => u.email.toLowerCase() === e) ?? null;
  const blocked = current ? roleChangeBlocked(current) : null;
  const sameRole = !!current && current.role === role;
  const ok = EMAIL_RE.test(e) && !blocked && !sameRole;

  async function save() {
    setBusy(true); setErr(null);
    try { await addUser(ctx.collector.id, e, role); }
    catch (ex: any) { setErr(String(ex?.message ?? ex)); setBusy(false); return; }
    if (current) { setBusy(false); await onDone(`${e} is now ${role}`); return; }
    // El usuario ya existe en CoreDB; los collectors adicionales van por webadmin2.
    let msg = "User added — they'll receive a login email";
    if (vendorIid != null && extra.some((i) => i !== vendorIid)) {
      try { await setUserCollectors(e, extra, { iid: vendorIid, multi_iid: null }); }
      catch (ex: any) { msg = `User added, but extra collectors were NOT saved (${ex?.message ?? ex}). Edit them from the list.`; }
    }
    setBusy(false);
    await onDone(msg);
  }
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Add user<Typography variant="body2" color="text.secondary">{ctx.collector.id} · vendor web access</Typography></DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Email" type="email" value={email} autoFocus disabled={busy} placeholder="name@company.com" onChange={(ev) => setEmail(ev.target.value)}
            error={!!blocked}
            helperText={!current ? " "
              : blocked ? `This email already has access to ${ctx.collector.id}. ${blocked}.`
              : `Already has access to ${ctx.collector.id} as ${current.role || "—"}: saving changes the role.`}
            sx={squareInput} />
          <Box>
            <Typography variant="caption" color="text.secondary">Role</Typography>
            <RadioGroup value={role} onChange={(ev) => setRole(ev.target.value)}>
              {USER_ROLES.map((r) => (
                <FormControlLabel key={r.key} value={r.key} disabled={busy} control={<Radio size="small" />}
                  sx={{ alignItems: "flex-start", mx: 0, my: 0.5, p: 1, border: 1, borderColor: role === r.key ? BRAND.primary : "divider", borderRadius: 2, bgcolor: role === r.key ? "rgba(91,47,235,0.05)" : undefined }}
                  label={<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>{r.key}</Typography><Typography variant="caption" color="text.secondary">{r.description}</Typography></Box>} />
              ))}
            </RadioGroup>
          </Box>
          {current && !blocked && role === "main" && !sameRole && (
            <Alert severity="warning">A main user can manage the other users, and its role can't be changed again from Web Admin.</Alert>
          )}
          {vendorIid != null && !current && (
            <Box>
              <CollectorPicker options={options} primary={vendorIid} value={extra} onChange={setExtra} disabled={busy} />
              <Typography variant="caption" color="text.secondary">{ctx.collector.id} is the main collector. Add others only for multi-branch users.</Typography>
            </Box>
          )}
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !ok} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
          {busy ? (current ? "Saving…" : "Adding…") : current ? "Change role" : "Add user"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditCollectorsDialog({ user, options, onClose, onDone }: { user: UserAccess | null; options: VendorOption[]; onClose: () => void; onDone: () => Promise<void> }) {
  const [value, setValue] = useState<number[]>([]);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState<string | null>(null);
  useEffect(() => { if (user) { setValue(user.collectors); setErr(null); } }, [user]);
  const primary = user?.iid ?? 0;
  const same = !!user && [...value].sort((a, b) => a - b).join(",") === [...user.collectors].sort((a, b) => a - b).join(",");

  async function save() {
    if (!user) return;
    setBusy(true); setErr(null);
    try { await setUserCollectors(user.email, value, { iid: user.iid, multi_iid: user.multi_iid }); await onDone(); }
    catch (ex: any) { setErr(String(ex?.message ?? ex)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={!!user} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Collectors<Typography variant="body2" color="text.secondary">{user?.email} · collectors this user can access</Typography></DialogTitle>
      <DialogContent>
        <Stack spacing={1} mt={1}>
          <CollectorPicker options={options} primary={primary} value={value} onChange={setValue} disabled={busy} />
          <Typography variant="caption" color="text.secondary">The main collector can't be removed. Changes apply on the user's next login.</Typography>
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || same} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>{busy ? "Saving…" : "Save"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function UnlockDialog({ user, onClose, onDone }: { user: CollectorUser | null; onClose: () => void; onDone: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  useEffect(() => { if (user) setErr(null); }, [user]);

  async function confirm() {
    if (!user) return;
    setBusy(true); setErr(null);
    try { await unlockUser(user.email); await onDone(); }
    catch (ex: any) { setErr(String(ex?.message ?? ex)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={!!user} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Unlock user<Typography variant="body2" color="text.secondary">{user?.email}</Typography></DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} mt={1}>
          <Typography variant="body2">
            Locked after <b>{user?.failedAttempts}</b> failed login attempts. Unlocking resets the counter to 0 and
            invalidates the unlock link sent by email.
          </Typography>
          <Typography variant="caption" color="text.secondary">Make sure the request comes from the user before unlocking. The action is logged with your email.</Typography>
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="error" onClick={confirm} disabled={busy} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <LockOpenIcon />}>{busy ? "Unlocking…" : "Unlock"}</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * CP-451: cuentas a las que está restringido el usuario (users.bank_account,
 * Id Cliente). Sin restricción → "All". Más de 2 → las 2 primeras + "+N" con
 * la lista completa en el tooltip.
 */
function AssignedAccounts({ ids }: { ids: string[] | null }) {
  if (!ids) return <Typography variant="body2" color="text.secondary">All</Typography>;
  const shown = ids.slice(0, 2), rest = ids.length - shown.length;
  return (
    <Tooltip title={ids.length > 1 ? ids.join(" · ") : ""}>
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {shown.map((id) => <StatusChip key={id} kind="info" label={id} />)}
        {rest > 0 && <StatusChip kind="neutral" label={`+${rest}`} />}
      </Stack>
    </Tooltip>
  );
}
